import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import AIManagersService from 'src/services/AIManagersService';

import type { AIManagerKnowledgeAnalysis } from 'src/services/AIManagersService';

type Input = { url: string; offerNotes: string };

export default function useKnowledgeAnalysis(
    scope: string,
    formatError: (error: unknown, fallback: string) => string,
    draft?: unknown
) {
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const sequence = useRef(0);
    const running = useRef<number | null>(null);
    const alive = useRef(false);

    const reset = useCallback(() => {
        sequence.current += 1;
        running.current = null;
        setPending(false);
        setError('');
    }, []);

    useLayoutEffect(() => {
        alive.current = true;
        reset();
        return () => {
            alive.current = false;
            sequence.current += 1;
            running.current = null;
        };
    }, [scope, draft, reset]);

    const run = async (input: Input, onSuccess: (result: AIManagerKnowledgeAnalysis) => void) => {
        if (!alive.current || running.current !== null) return;
        const token = ++sequence.current;
        running.current = token;
        setPending(true);
        setError('');
        const current = () => alive.current && token === sequence.current;
        try {
            const result = await AIManagersService.analyzeKnowledgeWebsite({
                url: input.url.trim(),
                offerNotes: input.offerNotes.trim(),
            });
            if (!current()) return;
            const hasFacts = result?.displayProfile?.sections?.some((section) =>
                section.items?.some((item) => typeof item.text === 'string' && item.text.trim())
            );
            if (!hasFacts) {
                setError('На сайте не нашлись факты для базы знаний. Добавьте описание вручную.');
                return;
            }
            onSuccess(result);
        } catch (reason) {
            if (current()) setError(formatError(reason, 'Не удалось проанализировать сайт. Попробуйте ещё раз.'));
        } finally {
            if (current()) {
                running.current = null;
                setPending(false);
            }
        }
    };

    return { pending, error, setError, reset, run };
}
